/**
 * Was nicht weiterkommt: die fehlgeschlagenen und die geparkten Prüfaufträge samt
 * Grund — und der Weg zurück in die Warteschlange (Spec, User Story 91; #94).
 *
 * Sie beantwortet die Frage, die ein Betreiber tatsächlich hat — *was muss ich
 * reparieren?* —, und deshalb stehen beide Zustände in einer Liste:
 *
 * - **Fehlgeschlagen** heißt, der Versuchszähler ist erschöpft; der Auftrag wird
 *   nicht mehr gezogen, und der Grund steht an ihm.
 * - **Geparkt** heißt, der Auftrag wartet weiter, aber sein Agent ist stillgelegt
 *   — in aller Regel, weil das aufgelöste Basismodell abwich (ADR-0005). Der
 *   Grund steht dann am Agenten, und der Weg zurück ist seine Freigabe im
 *   Agenten-Dialog.
 *
 * Ein Auftrag, der ganz gewöhnlich wartet, steht nicht darin: Er kommt gleich
 * dran, und eine Liste, in der er stünde, wäre die Warteschlange und keine Liste
 * der Störungen.
 *
 * **Der Knopf gehört der Gruppe, nicht der Zeile.** Im Betrieb stehen Hunderte
 * Aufträge an wenigen Ursachen; ein Knopf je Zeile hieße hundertmal klicken für
 * einen einzigen Vorgang. Ein Knopf über allem wäre das andere Extrem — er nähme
 * die dauerhaften Fehlschläge mit, die wortgleich wieder scheitern, und danach
 * sähe die Liste aus wie vorher.
 *
 * **Gruppiert wird über den Fehlertext, und das ist eine Gruppierung der
 * Anzeige.** Sie sortiert, was ein Mensch auswählt — sie entscheidet nichts. Die
 * maschinenlesbare Klasse eines Fehlschlags entsteht anderswo; sobald sie am
 * Auftrag steht, wandert die Gruppierung auf sie und der Fehlertext bleibt Beleg.
 *
 * **Der Knopf verspricht keine Behebung.** Eine Gruppe „Malformed UTF-8" lässt
 * sich einreihen und scheitert wieder, solange der Bestand nicht umkodiert ist.
 * Die Fläche behauptet deshalb nichts über den Erfolg, sondern nennt, was
 * eingereiht wurde.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { Button, Chip, DataTable, IconButton, StatusPill } from "../../design-system";
import type { AdminApi, RequeueTally, StalledJob } from "../../api/client";
import { formatNumber } from "../../formatting";
import { NumericCell, StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";
import { KIND_LABELS } from "../reviewJobs";

/** Nur die Wege, die dieser Abschnitt greift. */
type ReviewJobsClient = Pick<AdminApi, "stalledJobs" | "requeueJob" | "requeueJobsWithError">;

/**
 * Eine Gruppe der Anzeige: alles, was denselben Grund nennt.
 *
 * `requeueable` ist die Teilmenge, an der der Knopf wirkt — fehlgeschlagen und
 * nicht am geparkten Agenten. Sie steht getrennt von `jobs`, weil die Zahl der
 * Zeilen und die Zahl der Knöpfe auseinanderfallen dürfen.
 */
interface JobGroup {
  reason: string | null;
  jobs: StalledJob[];
  requeueable: StalledJob[];
}

export function ReviewJobsSection({
  client,
}: {
  // Nur die Wege, die dieser Abschnitt greift. Die Rollenprüfung hängt nicht
  // daran, sondern am Pfad: `/api/admin/*` sieht ausschließlich `admin`.
  client: ReviewJobsClient;
}): ReactElement {
  const { state, reload } = useLoaded(() => client.stalledJobs());

  /** Was der letzte Knopfdruck ausgerichtet hat — eine Auskunft, keine Störung. */
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const jobs = state.kind === "loaded" ? state.value : [];
  const groups = groupsOf(jobs);

  /**
   * Ein Knopfdruck und was danach dasteht. Gelingt er, wird die Liste neu geladen —
   * sonst stünde die Gruppe weiter da, die es gerade nicht mehr gibt. Scheitert
   * er, bleibt die Liste, wie sie war, und der Wortlaut des Servers steht
   * darüber.
   */
  const act = (action: Promise<string>): void => {
    setNote(null);
    setFailure(null);

    action.then(
      (told) => {
        setNote(told);
        reload();
      },
      (error: unknown) => setFailure(describe(error)),
    );
  };

  return (
    <ListSection
      title="Liegengebliebene Prüfaufträge"
      glyph="triangle-alert"
      headerRight={
        state.kind === "loaded" ? <Chip>{`${formatNumber(jobs.length)} offen`}</Chip> : null
      }
    >
      <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
        {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
        {state.kind === "failed" ? (
          <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
        ) : null}
        {failure === null ? null : (
          <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
        )}
        {note === null ? null : <Notice text={note} glyph="list-plus" />}
        {state.kind === "loaded" && jobs.length === 0 ? (
          <Notice text="Kein Auftrag ist liegengeblieben." glyph="check" />
        ) : null}
      </div>

      {groups.map((group) => (
        <GroupRow
          key={group.reason ?? ""}
          group={group}
          requeueGroup={() => client.requeueJobsWithError(group.reason ?? "").then(tallyOf)}
          requeueJob={(id) =>
            client.requeueJob(id).then(() => "Der Prüfauftrag ist erneut eingereiht.")
          }
          act={act}
        />
      ))}
    </ListSection>
  );
}

/**
 * Eine Gruppe: die Zeile mit Zahl, Zustand, Beleg und Knopf — und aufgeklappt die
 * einzelnen Aufträge.
 *
 * Der Fehlertext steht **einmal** je Gruppe und nicht an jeder Zeile: Innerhalb
 * einer Gruppe ist er derselbe, und die Breite, die er dort verbrauchte, fehlte
 * dem Pfad.
 */
function GroupRow({
  group,
  requeueGroup,
  requeueJob,
  act,
}: {
  group: JobGroup;
  requeueGroup: () => Promise<string>;
  requeueJob: (id: number) => Promise<string>;
  act: (action: Promise<string>) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const reason = group.reason ?? "Kein Grund vermerkt.";
  const parked = group.jobs.every((job) => job.parked);

  return (
    <div style={{ borderTop: "1px solid var(--line-f)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-5)",
          padding: "var(--sp-4) var(--sp-5)",
        }}
      >
        <Button
          variant="outline"
          size="sm"
          title={open ? "Einklappen" : "Aufklappen"}
          icon={open ? "chevron-down" : "chevron-right"}
          onClick={() => setOpen((previous) => !previous)}
        >
          {countOf(group.jobs.length, "Auftrag", "Aufträge")}
        </Button>

        {parked ? (
          <StatusPill tone="amber">geparkt</StatusPill>
        ) : (
          <StatusPill tone="red">fehlgeschlagen</StatusPill>
        )}

        {/* Der Beleg. `DataTable` bricht nicht um, und diese Zeile soll es auch
            nicht: Der ganze Satz steht im Titel. */}
        <span
          title={reason}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "var(--fs-12)",
            color: "var(--fg-6)",
          }}
        >
          {reason}
        </span>

        {/* Ohne vermerkten Grund gibt es keinen Zuschnitt: Der Sammelvorgang
            wählt über genau diesen Text aus, und eine leere Auswahl wäre kein
            Zuschnitt, sondern „alles". Dann bleibt der Knopf an der Zeile. */}
        {group.requeueable.length === 0 || group.reason === null ? null : (
          <Button
            variant="outline"
            size="sm"
            title={`Alle ${countOf(group.requeueable.length, "Prüfauftrag", "Prüfaufträge")} mit diesem Fehlertext erneut einreihen`}
            icon="rotate-ccw"
            onClick={() => act(requeueGroup())}
          >
            Erneut einreihen
          </Button>
        )}
      </div>

      {open ? (
        <DataTable
          columns={[
            { key: "state", label: "Zustand", width: 130 },
            { key: "agent", label: "Agent", width: 160, maxWidth: 160 },
            { key: "kind", label: "Art", width: 130 },
            { key: "revision", label: "Revision", width: 110, align: "right" },
            // Der Pfad ist der freie Text dieser Tabelle, und `DataTable` bricht
            // nicht um: Ohne eine Breite neben der Deckelung zöge ein langer Pfad
            // die Tabelle beliebig breit. Er endet deshalb in einer Ellipse und
            // steht ganz im Titel seiner Zelle.
            { key: "path", label: "Datei", width: 300, maxWidth: 300 },
            { key: "attempts", label: "Versuche", width: 100, align: "right" },
            { key: "actions", label: "", width: 90, align: "right" },
          ]}
          rows={group.jobs.map((job) => ({
            cells: {
              state: job.parked ? (
                <StatusPill tone="amber">geparkt</StatusPill>
              ) : (
                <StatusPill tone="red">fehlgeschlagen</StatusPill>
              ),
              // Wiedererkannt wird die Zeile am Agenten und nicht am Zustand
              // davor — deshalb trägt sie hier und nicht vorn die Hülle.
              agent: <StrongCell>{job.agentName}</StrongCell>,
              kind: KIND_LABELS[job.kind] ?? job.kind,
              revision: (
                <NumericCell>
                  {job.revisionNumber === null ? "—" : formatNumber(job.revisionNumber)}
                </NumericCell>
              ),
              // Eine Synthese hängt an der Revision und nicht an einer Datei.
              path: <span title={job.path ?? "—"}>{job.path ?? "—"}</span>,
              attempts: <NumericCell>{formatNumber(job.attempts)}</NumericCell>,
              // An einer Zeile, die wegen eines geparkten Agenten steht, gehört der
              // Weg über den Agenten hin und nicht dieser: Eingereiht würde sie
              // ohnehin nicht gezogen.
              actions:
                job.state === "failed" && !job.parked ? (
                  <IconButton
                    icon="rotate-ccw"
                    tip="Diesen Prüfauftrag erneut einreihen"
                    size="sm"
                    tone="quiet"
                    onClick={() => act(requeueJob(job.id))}
                  />
                ) : null,
            },
          }))}
        />
      ) : null}
    </div>
  );
}

/**
 * Die Gruppen in der Reihenfolge, in der ihr erster Auftrag kommt.
 *
 * Der Schlüssel ist der Grund, wie die Liste ihn nennt — beim fehlgeschlagenen
 * Auftrag sein Fehlertext, beim geparkten der Parkgrund seines Agenten. Zwei
 * Ursachen sind darin sauber unterscheidbar, und genau dafür ist die Gruppe da.
 */
function groupsOf(jobs: StalledJob[]): JobGroup[] {
  const groups: JobGroup[] = [];

  for (const job of jobs) {
    const found = groups.find((group) => group.reason === job.reason);
    const group = found ?? { reason: job.reason, jobs: [], requeueable: [] };

    group.jobs.push(job);

    if (job.state === "failed" && !job.parked) {
      group.requeueable.push(job);
    }

    if (found === undefined) {
      groups.push(group);
    }
  }

  return groups;
}

/** „1 Auftrag", „128 Aufträge" — die Zahl mit dem Wort, das zu ihr passt. */
function countOf(amount: number, one: string, many: string): string {
  return `${formatNumber(amount)} ${amount === 1 ? one : many}`;
}

/**
 * Was der Sammelvorgang ausgerichtet hat, als Satz.
 *
 * Die übersprungenen stehen **gesondert** dabei und mit dem Weg, der bei ihnen
 * weiterhilft: Ohne diesen Satz klickt jemand einen Knopf, der für einen Teil der
 * Gruppe schweigend nichts tut.
 */
function tallyOf(tally: RequeueTally): string {
  const requeued =
    tally.requeued === 0
      ? "Kein Prüfauftrag war erneut einzureihen."
      : tally.requeued === 1
        ? "Ein Prüfauftrag ist erneut eingereiht."
        : `${formatNumber(tally.requeued)} Prüfaufträge sind erneut eingereiht.`;

  if (tally.skippedParked === 0) {
    return requeued;
  }

  return (
    `${requeued} ` +
    (tally.skippedParked === 1
      ? "Ein Auftrag eines geparkten Agenten blieb liegen — erst den Agenten freigeben."
      : `${formatNumber(tally.skippedParked)} Aufträge geparkter Agenten blieben liegen — erst die Agenten freigeben.`)
  );
}
